// lightTypes — the lights in the scene as OBJECTS (ENG-1190).
//
// Before this module every light was a fixed slot in liveTuneStore: `shadow`
// (the sun, mixed in with the catcher plane it casts onto), `spotlight` /
// `spotlight2` / `spotlight3`, `torch`, `hemisphere`. Each had its own panel
// section and nothing else; there was no way to have a fourth spot or no sun.
//
// Now `liveTuneStore.lights` is a MAP of id → entry, each entry `{ type, name,
// enabled, ...typeFields }`. A map rather than an array because every piece of
// store machinery — deepMerge, authoredPathsIn / stripPaths, diffTune's
// flatten, computeDiff — walks plain objects and treats arrays as leaves, so an
// array would have needed array-aware special cases in all of them. Insertion
// order is the outliner order; JSON and `{ ...base }` both preserve it.
//
// This module owns three pure things, so `node --test` can pin them:
//   - the per-type DEFAULTS (the old slots' values verbatim, so an untouched
//     install renders bit-identically);
//   - normalizeLights, which makes a partial entry whole — the store runs it
//     after every merge into `lights`, which is what lets
//     `setState({ lights: { torch: { enabled: true } } })` produce a complete
//     torch instead of an entry with one key;
//   - migrateLegacyLights, the one-way conversion of the old slots, applied to
//     a stored v4 blob, to a `tp` URL carrier, and to an environment row's
//     `default_tune` saved before this ticket.

import { SCENE_WHITE, HEMI_GROUND } from './sceneColorDefaults.js'

export const LIGHT_TYPES = Object.freeze(['directional', 'spot', 'point', 'hemisphere'])

export const LIGHT_DEFAULTS = Object.freeze({
  // The shadow-casting "sun". Orbits the head probe on a sphere (lightPlacement.js);
  // its direction also drives the live key light (lighting.keyLightIntensity).
  // Intensity 0 is deliberate and old: by default it only CASTS, the IBL lights.
  directional: Object.freeze({
    type: 'directional', name: 'Sun', enabled: true,
    intensity: 0,
    // Colour temperature in Kelvin; 6500 ≈ neutral. Mapped by colorTemperature.js.
    temperature: 6500,
    distance: 5.74, elevation: 60, azimuth: 45,
    // Its own VSM shadow map. mapSize used to sit in the panel's SHADOW section
    // "because it's global" — it never was; it is this light's map. 1024 since
    // ENG-1230 (was 2048): over the 3 m frustum below that is ~340 texels/m,
    // and the shadow is blurred by `shadowRadius` over 30 samples before anyone
    // sees it, so the resolution above it was paying for detail the blur threw
    // away. 2k and 4k stay selectable for a scene that wants them.
    shadowRadius: 12, shadowBlurSamples: 30, shadowMapSize: 1024, shadowCameraSize: 3, shadowBias: -0.0005,
  }),
  // Orbit rig: a group at the origin yaws about Y, the light sits at local
  // [0, height, distance] aiming at a target at world [0, targetHeight, 0].
  spot: Object.freeze({
    type: 'spot', name: 'Spot', enabled: false,
    color: SCENE_WHITE,
    intensity: 50,       // candela (decay 2)
    range: 0,            // THREE SpotLight.distance; 0 = infinite
    angle: 30,           // cone half-angle, degrees
    penumbra: 0.3,
    distance: 4, height: 3, yaw: 0, targetHeight: 1.4,
    // A free aim, set by the rotate gizmo (XYZ Euler, radians). null = the
    // rig's own aim: the point [0, targetHeight, 0] on the central axis. Once
    // set, the beam points where you turned it and targetHeight is inert until
    // "Aim at character" clears it again.
    aimX: null, aimY: null, aimZ: null,
    gizmo: false,        // THREE.SpotLightHelper cone, always (selection shows it too)
    // CAST SHADOWS AT ALL. A shadow-casting spotlight is a whole extra render of
    // the scene per frame plus two VSM blur passes over its map, at the map's own
    // resolution — so it is PIXEL-INDEPENDENT and does not get cheaper in a small
    // window. It is the single largest cost an environment's own `default_tune`
    // can switch on; `?spotshadows=0` is the escape hatch (SpotlightRig.jsx).
    castShadow: true,
    shadowRadius: 12, shadowBlurSamples: 30, shadowMapSize: 1024, shadowBias: -0.0005,
  }),
  // A movable point light with an unlit emitter cube — the "walk a light past
  // the subject and watch the highlight move" probe (was `torch`).
  point: Object.freeze({
    type: 'point', name: 'Point light', enabled: false,
    color: SCENE_WHITE,
    intensity: 1.5,      // candela (decay 2)
    range: 0,
    targetHeight: 1.4, distance: 0.8, elevation: 10, azimuth: 35,
    cube: true, cubeSize: 0.06,
    followCamera: false, // headlamp: rides the camera, owns its position
  }),
  // THREE.HemisphereLight — sky/ground ambient fill. Sky and ground intensities
  // are faked by pre-multiplying each colour (three has one shared intensity).
  hemisphere: Object.freeze({
    type: 'hemisphere', name: 'Hemisphere', enabled: false,
    skyColor: SCENE_WHITE, skyIntensity: 1,
    groundColor: HEMI_GROUND, groundIntensity: 0.5,
    // The sky DIRECTION (three derives it from the light's position). 90/0 is
    // straight up — the HemisphereLight default — so an untouched entry renders
    // as before; the rotate gizmo tilts it.
    elevation: 90, azimuth: 0,
  }),
})

/** The six fixed slots' ids, and what kind each was. Used to infer a missing
 *  `type` (the e2e specs and old console habits patch `lights.torch` bare) and
 *  as the target ids of the migration below. */
export const LEGACY_LIGHT_TYPE = Object.freeze({
  sun: 'directional', spot1: 'spot', spot2: 'spot', spot3: 'spot', torch: 'point', hemi: 'hemisphere',
})

// The old slots' pre-spread yaw, so migrating (or re-creating) slot 2 / 3 does
// not stack them on slot 1.
const LEGACY_SPOT_YAW = { spot1: 0, spot2: 130, spot3: -130 }

function clone(v) {
  if (Array.isArray(v)) return v.map(clone)
  if (v && typeof v === 'object') {
    const out = {}
    for (const k of Object.keys(v)) out[k] = clone(v[k])
    return out
  }
  return v
}

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v)

/** A fresh, complete default entry of `type`. Throws on an unknown type: a typo
 *  must not create a light of no kind that nothing can render. */
export function defaultLight(type) {
  const d = LIGHT_DEFAULTS[type]
  if (!d) throw new Error(`unknown light type "${type}"`)
  return clone(d)
}

/**
 * Every entry made whole from its type's defaults, in the input's order.
 *
 * An entry with no `type` takes the type its LEGACY id implies; one whose type
 * is still unknown is dropped rather than kept as a shape nothing renders.
 * Junk input (null, array, non-object entries) yields `{}`.
 */
export function normalizeLights(map) {
  const out = {}
  if (!isPlain(map)) return out
  for (const id of Object.keys(map)) {
    const e = map[id]
    if (!isPlain(e)) continue
    const type = e.type ?? LEGACY_LIGHT_TYPE[id]
    if (!LIGHT_DEFAULTS[type]) continue
    const base = defaultLight(type)
    if (type === 'spot' && id in LEGACY_SPOT_YAW) base.yaw = LEGACY_SPOT_YAW[id]
    out[id] = { ...base, ...clone(e), type }
  }
  return out
}

// ── migration from the fixed slots ─────────────────────────────────────────

// Sun fields that lived in `shadow`, old name → new name. Everything else in
// `shadow` (opacity, floorYOverride) is the catcher plane and stays.
const SHADOW_SUN_FIELDS = Object.freeze({
  enabled: 'enabled',
  lightIntensity: 'intensity',
  temperature: 'temperature',
  lightDistance: 'distance',
  lightElevation: 'elevation',
  lightAzimuth: 'azimuth',
  radius: 'shadowRadius',
  blurSamples: 'shadowBlurSamples',
  mapSize: 'shadowMapSize',
  cameraSize: 'shadowCameraSize',
  bias: 'shadowBias',
})

const LEGACY_SLOTS = Object.freeze([
  ['spotlight', 'spot1'],
  ['spotlight2', 'spot2'],
  ['spotlight3', 'spot3'],
  ['torch', 'torch'],
  ['hemisphere', 'hemi'],
])

/**
 * The biggest shadow map a light may arrive with from a look saved BEFORE
 * ENG-1230 (the fixed-slot era). 4096 was never a default — the spot slot
 * shipped at 1024 and the sun at 2048 — but every environment saved in that era
 * carries 4k on all three spots, and several carry 4k on the sun too. An
 * environment with three enabled spots then renders three 4096² VSM passes, the
 * most expensive thing in the frame.
 *
 * Deliberately a MIGRATION cap, not a global clamp: 4k stays selectable, so a
 * light someone sets to 4k today keeps it. Only the legacy shape — which is
 * exactly "saved before this" — is lowered on the way in.
 */
export const LEGACY_SHADOW_MAP_CAP = 1024
/** @deprecated the cap covers every shadow-casting light, not just spots. */
export const LEGACY_SPOT_SHADOW_CAP = LEGACY_SHADOW_MAP_CAP

const cappedShadowMap = (size) => (
  Number.isFinite(size) && size > LEGACY_SHADOW_MAP_CAP ? LEGACY_SHADOW_MAP_CAP : size
)

/** Does this tune still carry any of the old light slots? */
export function hasLegacyLightKeys(tune) {
  if (!isPlain(tune)) return false
  if (LEGACY_SLOTS.some(([old]) => old in tune)) return true
  const sh = tune.shadow
  return isPlain(sh) && Object.keys(SHADOW_SUN_FIELDS).some(k => k in sh)
}

// A slot is worth carrying over when someone turned it on OR changed any of
// its values. An untouched, disabled slot was never a light anyone had; keeping
// it would make every migrated outliner open with five dead rows.
function slotIsMeaningful(slot, type, id) {
  if (!isPlain(slot)) return false
  if (slot.enabled) return true
  const base = LIGHT_DEFAULTS[type]
  for (const k of Object.keys(slot)) {
    if (k === 'enabled') continue
    const ref = (type === 'spot' && k === 'yaw' && id in LEGACY_SPOT_YAW) ? LEGACY_SPOT_YAW[id] : base[k]
    if (JSON.stringify(slot[k]) !== JSON.stringify(ref)) return true
  }
  return false
}

/**
 * The old fixed slots → `lights`, one way, without touching the input.
 *
 * - `shadow`'s sun fields become `lights.sun` whenever a `shadow` slice exists
 *   (the sun exists by default, so it is never dropped); the catcher fields stay.
 * - Each other slot becomes an entry only if it was enabled or tuned.
 * - Legacy keys are removed from the result.
 * - A tune that already has `lights` keeps it untouched (idempotent) — only
 *   the legacy keys are stripped.
 * - With nothing legacy at all, no `lights` key is added: the caller merges
 *   DEFAULTS and gets the default sun that way.
 *
 * Order of the produced entries is sun, spot1, spot2, spot3, torch, hemi — the
 * order the panel listed them, so a migrated outliner reads like the panel did.
 */
export function migrateLegacyLights(tune) {
  if (!isPlain(tune)) return tune
  const out = clone(tune)
  const already = isPlain(out.lights)
  const anySlot = LEGACY_SLOTS.some(([old]) => old in out)
  const lights = {}

  if (isPlain(out.shadow)) {
    const sun = {}
    const catcher = {}
    for (const k of Object.keys(out.shadow)) {
      if (k in SHADOW_SUN_FIELDS) sun[SHADOW_SUN_FIELDS[k]] = out.shadow[k]
      else catcher[k] = out.shadow[k]
    }
    out.shadow = catcher
    if (!already && Object.keys(sun).length) {
      const entry = { ...defaultLight('directional'), ...sun }
      entry.shadowMapSize = cappedShadowMap(entry.shadowMapSize)
      lights.sun = entry
    }
  }
  // A legacy tune that carried slots but no sun fields still HAD a sun (it
  // existed by default and nobody could delete it), so the converted map says
  // so — a saved map replaces the live one wholesale, and a missing sun there
  // would read as deleted.
  if (!already && anySlot && !lights.sun) lights.sun = defaultLight('directional')

  for (const [old, id] of LEGACY_SLOTS) {
    if (!(old in out)) continue
    const slot = out[old]
    delete out[old]
    if (already) continue
    const type = LEGACY_LIGHT_TYPE[id]
    if (!slotIsMeaningful(slot, type, id)) continue
    const base = defaultLight(type)
    if (type === 'spot') base.yaw = LEGACY_SPOT_YAW[id]
    const entry = { ...base, ...clone(slot) }
    if ('shadowMapSize' in entry) entry.shadowMapSize = cappedShadowMap(entry.shadowMapSize)
    lights[id] = entry
  }

  if (!already && Object.keys(lights).length) out.lights = lights
  return out
}

/** The first light of `type` in outliner order, or null. The scene asks this
 *  for "the sun" — the key light follows whichever directional comes first. */
export function firstLight(lights, type) {
  if (!isPlain(lights)) return null
  for (const id of Object.keys(lights)) {
    if (lights[id]?.type === type) return lights[id]
  }
  return null
}

/** Is any light of `type` (or of any type, when omitted) enabled? */
export function anyLightEnabled(lights, type = null) {
  if (!isPlain(lights)) return false
  return Object.values(lights).some(l => l?.enabled && (type == null || l.type === type))
}

/** A fresh id for a new light of `type`, unique against `lights`. */
export function nextLightId(lights, type) {
  const taken = new Set(Object.keys(lights ?? {}))
  let n = 1
  while (taken.has(`${type}_${n}`)) n += 1
  return `${type}_${n}`
}

/** "Spot 2"-style display name: the type's default name, numbered when one
 *  of that type already exists. */
export function nextLightName(lights, type) {
  const base = LIGHT_DEFAULTS[type]?.name ?? type
  const count = Object.values(lights ?? {}).filter(l => l?.type === type).length
  return count === 0 ? base : `${base} ${count + 1}`
}
